// src/Dashboard/pages/user/Boutique/BoutiqueDashboard.jsx

import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useBoutiqueDashboard } from "./hooks/useBoutiqueDashboard";

import BoutiqueHeader from "./components/BoutiqueHeader";
import BoutiqueKpis from "./components/BoutiqueKpis";
import BoutiqueToolbar from "./components/BoutiqueToolbar";
import BoutiqueArticleCard from "./components/BoutiqueArticleCard";
import BoutiqueArticleDrawer from "./components/BoutiqueArticleDrawer";
import CartDrawer from "./components/CartDrawer";
import BoutiqueSkeleton from "./components/BoutiqueSkeleton";
import BoutiqueEmptyState from "./components/BoutiqueEmptyState";

export default function BoutiqueDashboard() {
  useScrollToTop();

  const {
    user,

    articles,
    filteredArticles,
    visibleArticles,
    categories,
    loading,
    stats,

    ui,
    debouncedSearch,
    setDebouncedSearch,
    updateUi,
    resetFilters,
    loadMore,

    cart,
    setCart,
    cartOpen,
    openCart,
    closeCart,
    addToCart,
    removeFromCart,

    selectedArticle,
    articleDrawerOpen,
    openArticleDrawer,
    closeArticleDrawer,

    goToReglements,
    refresh,
  } = useBoutiqueDashboard();

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <BoutiqueHeader
          loading={loading}
          cartUnits={stats.cartUnits}
          cartTotal={stats.cartTotal}
          onRefresh={refresh}
          onOpenCart={openCart}
          onGoReglements={goToReglements}
        />

        <BoutiqueKpis stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Boutique du club
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Articles disponibles
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Sélectionnez vos articles, ajoutez-les au panier, puis
                  validez votre commande. Le règlement sera ensuite disponible
                  dans votre espace Commandes & Règlements.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredArticles.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {articles.length}
                </span>
              </div>
            </div>

            <div className="mt-5">
              <BoutiqueToolbar
                ui={ui}
                categories={categories}
                updateUi={updateUi}
                debouncedSearch={debouncedSearch}
                setDebouncedSearch={setDebouncedSearch}
                onReset={resetFilters}
              />
            </div>

            {loading ? (
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {Array.from({ length: 8 }).map((_, index) => (
                  <BoutiqueSkeleton key={index} />
                ))}
              </div>
            ) : filteredArticles.length === 0 ? (
              <BoutiqueEmptyState onReset={resetFilters} />
            ) : (
              <>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {visibleArticles.map((article) => (
                    <BoutiqueArticleCard
                      key={article.id}
                      article={article}
                      onOpen={() => openArticleDrawer(article)}
                      onAdd={() => addToCart(article)}
                    />
                  ))}
                </div>

                {visibleArticles.length < filteredArticles.length && (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMore}
                      className="rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
                    >
                      Charger plus
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <BoutiqueArticleDrawer
        isOpen={articleDrawerOpen}
        article={selectedArticle}
        onClose={closeArticleDrawer}
        onAdd={addToCart}
      />

      <CartDrawer
        isOpen={cartOpen}
        cart={cart}
        setCart={setCart}
        user={user}
        onClose={closeCart}
        onRemove={removeFromCart}
        onOrderSuccess={refresh}
        onGoReglements={goToReglements}
      />

      <ToastContainer />
    </section>
  );
}